<h2 mat-dialog-title class="dialog-title">
  <mat-icon>precision_manufacturing</mat-icon>
  <span>Auto-Buy Stock Leases</span>
  <span class="spacer"></span>
  <button mat-icon-button (click)="load()" matTooltip="Refresh">
    <mat-icon>refresh</mat-icon>
  </button>
</h2>

<mat-dialog-content>
  <div class="fleet-summary">
    <div class="fleet-stat">
      <span class="fleet-label">Dial</span>
      <span class="fleet-value" matTooltip="How many stocks the admin allows to drain at once">
        {{ fleet.desired_concurrent_stocks }}
      </span>
    </div>
    <div class="fleet-stat">
      <span class="fleet-label">Running</span>
      <span class="fleet-value">{{ fleet.active_leases }}</span>
    </div>
    <div class="fleet-stat">
      <span class="fleet-label">Cards Bought</span>
      <span class="fleet-value">{{ totalCardsBought }}</span>
    </div>
    <div class="fleet-stat">
      <span class="fleet-label">Suppliers</span>
      <span class="fleet-value fleet-value--text">
        {{ fleet.supplier_scope === 'selected' ? fleet.supplier_ids.length + ' selected' : 'all' }}
      </span>
    </div>
  </div>

  @if (loading) {
  <div class="loading-state">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
  } @else if (error) {
  <div class="empty-state error-text">{{ error }}</div>
  } @else if (!leases.length) {
  <div class="empty-state">
    No stock leases yet. The scheduler leases a stock as soon as the contract has enough allocated
    cards to feed one.
  </div>
  } @else {
  <div class="table-container">
    <table mat-table [dataSource]="leases" [trackBy]="trackByLeaseId">

      <ng-container matColumnDef="stock">
        <th mat-header-cell *matHeaderCellDef>Stock</th>
        <td mat-cell *matCellDef="let l">
          <span class="mono" [matTooltip]="l.stock_id">{{ shortId(l.stock_id) }}</span>
          @if (l.session_id) {
          <div class="sub-value mono" [matTooltip]="'Session ' + l.session_id">{{ shortId(l.session_id) }}</div>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let l">
          <span [class]="'lease-badge ' + getStatusClass(l.status)"
            [matTooltip]="l.failed_reason ? 'Fail reason: ' + l.failed_reason : undefined">
            {{ l.status }}
          </span>
          @if (isResting(l)) {
          <span class="lease-badge lease-resting"
            [matTooltip]="'EA rate-limited — resting until ' + formatDate(l.rest_until) + '. The lease is slower, not dead.'">
            resting
          </span>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="coins">
        <th mat-header-cell *matHeaderCellDef>
          Coins
          <div class="sub-header">start → now / floor</div>
        </th>
        <td mat-cell *matCellDef="let l">
          <div>{{ formatCoins(l.start_coins) }} → {{ formatCoins(l.last_coins) }}</div>
          <div class="sub-value" matTooltip="The lease never spends the stock below its floor">
            floor {{ formatCoins(l.coins_floor) }} · drained {{ formatCoins(drainedCoins(l)) }}
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="cards">
        <th mat-header-cell *matHeaderCellDef>Cards</th>
        <td mat-cell *matCellDef="let l">{{ l.cards_bought }}</td>
      </ng-container>

      <ng-container matColumnDef="timing">
        <th mat-header-cell *matHeaderCellDef>
          Timing
          <div class="sub-header">start / finish</div>
        </th>
        <td mat-cell *matCellDef="let l">
          <div class="time">{{ formatDate(l.started_at) }}</div>
          <div class="time">
            @if (l.finished_at) {
            {{ formatDate(l.finished_at) }}
            } @else {
            <span class="sub-value" [matTooltip]="'Last heartbeat: ' + formatDate(l.heartbeat_at)">
              running
            </span>
            }
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="screenshots">
        <th mat-header-cell *matHeaderCellDef>Screenshots</th>
        <td mat-cell *matCellDef="let l">
          <button mat-icon-button (click)="viewScreenshot(l, 'start')"
            matTooltip="Stock credit at the start of the run">
            <mat-icon>photo_camera</mat-icon>
          </button>
          <button mat-icon-button (click)="viewScreenshot(l, 'end')"
            matTooltip="Stock credit at the end of the run">
            <mat-icon>photo_camera_back</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>
  </div>

  <div class="lease-cards">
    @for (l of leases; track l.id) {
    <div class="lease-card">
      <div class="lease-card-top">
        <span class="mono">{{ shortId(l.stock_id) }}</span>
        <span [class]="'lease-badge ' + getStatusClass(l.status)">{{ l.status }}</span>
        @if (isResting(l)) {
        <span class="lease-badge lease-resting">resting</span>
        }
      </div>
      <div class="lease-card-row">
        <span class="label">Coins</span>
        <span>{{ formatCoins(l.start_coins) }} → {{ formatCoins(l.last_coins) }}</span>
      </div>
      <div class="lease-card-row">
        <span class="label">Cards</span>
        <span>{{ l.cards_bought }}</span>
      </div>
      <div class="lease-card-row">
        <span class="label">Started</span>
        <span>{{ formatDate(l.started_at) }}</span>
      </div>
      <div class="lease-card-row">
        <span class="label">Finished</span>
        <span>{{ l.finished_at ? formatDate(l.finished_at) : 'running' }}</span>
      </div>
      @if (l.failed_reason) {
      <div class="lease-card-row">
        <span class="label">Failed</span>
        <span class="error-text">{{ l.failed_reason }}</span>
      </div>
      }
      <div class="lease-card-actions">
        <button mat-button (click)="viewScreenshot(l, 'start')">
          <mat-icon>photo_camera</mat-icon> Start
        </button>
        <button mat-button (click)="viewScreenshot(l, 'end')">
          <mat-icon>photo_camera_back</mat-icon> End
        </button>
      </div>
    </div>
    }
  </div>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="close()">Close</button>
</mat-dialog-actions>
